import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    groups: [],
    groups_filters: DEFAULT_FILTERS,
    groups_count: 0,
    groups_loaded: false,
    groups_loading: false,
    groups_loading_errors: null,

    group_deleting: false,
    group_deleting_errors: null,
    cancelTokenSource: null,
    _storageOptions: options || {},
});


const mutations = {
    updateGroups(state, groups) {
        state.groups = groups;
        state.groups_loaded = true;
    },
    updateGroupsCount(state, count) {
        state.groups_count = count;
    },
    updateGroupsFilters(state, filters) {
        state.groups_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.groups_filters, fieldsToSave);
        }
    },
    updateGroupsLoading(state, loading) {
        state.groups_loading = loading;
    },
    updateGroupsLoadingErrors(state, errors) {
        state.groups_loading_errors = errors;
    },

    updateGroupDeleting(state, deleting) {
        state.group_deleting = deleting;
    },
    updateGroupDeletingErrors(state, errors) {
        state.group_deleting_errors = errors;
    },
    updateGroupsLoaded(state, loaded) {
        state.groups_loaded = loaded;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    exportGroupsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.groups_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/groups/",
            params: filters,
        }, $utils.getFilename('groups'));
    },

    async deleteGroup({ commit, dispatch, state }, params) {
        const url = `/api/groups/${params.instance.id}/`;

        commit('updateGroupDeleting', true);
        commit('updateGroupDeletingErrors', null);

        try {
            const response = await axios.delete(url);
            commit('updateGroupDeleting', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateGroupDeleting', false);
            commit('updateGroupDeletingErrors', error.details);
            throw error;
        }
    },

    async fetchGroups({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateGroupsLoading', true);
        commit('updateGroupsLoadingErrors', null);

        try {
            const response = await axios.get('/api/groups/', {
                params: state.groups_filters,
                cancelToken: source.token,
            });
            commit('updateGroupsLoading', false);
            commit('updateGroups', response.data.results);
            commit('updateGroupsCount', response.data.count);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateGroupsLoadingErrors', error.details);
            commit('updateGroupsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateGroupsLoaded', false);
        commit('updateGroups', []);
        commit('updateGroupsCount', 0);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateGroupsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
    }
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});